import React, { useEffect, useState } from 'react';
import { Text, Stack, Group, Button, Avatar, Badge, LoadingOverlay, Alert, Center, Paper } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { IconCheck, IconX, IconBell, IconBuilding, IconInbox, IconInfoCircle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
import { getReceivedInvitations, acceptInvitation, OrganizationInvite, InvitationNotification } from '@mygpt/app';
import { useLogin } from '@/hooks/useLogin';
import { useAdminStore } from '@/store/admin';

const MessageCenter: React.FC = () => {
  const { t } = useTranslation();
  const { fetchCompanyId, companyId, fetchUnreadMessageCount } = useAdminStore();
  const { updateToken } = useLogin();
  const [loading, setLoading] = useState(true);
  const [invitations, setInvitations] = useState<OrganizationInvite[]>([]);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [error, setError] = useState('');

  const loadInvitations = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getReceivedInvitations();
      const notifications: InvitationNotification[] = response.data || [];
      setInvitations(notifications.map((n) => n.invitation));
      fetchUnreadMessageCount();
    } catch (err: any) {
      setError(t('messageCenter.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvitations();
  }, []);

  const handleAccept = async (invitation: OrganizationInvite) => {
    if (companyId) {
      setError(t('messageCenter.alreadyHasOrg'));
      return;
    }
    setProcessingId(invitation.id);
    setError('');
    try {
      const res = await acceptInvitation(invitation.id);
      if (res.data?.access_token) updateToken(res.data);
      await fetchCompanyId();
      showNotification({
        message: t('messageCenter.acceptSuccess'),
        color: 'green',
        icon: <IconCheck size={16} />,
      });
      setInvitations((prev) => prev.filter((inv) => inv.id !== invitation.id));
      fetchUnreadMessageCount();
    } catch (err: any) {
      setError(err?.message || t('messageCenter.acceptFailed'));
    } finally {
      setProcessingId(null);
    }
  };

  const pendingInvitations = invitations.filter((inv) => inv.status === 'pending');

  return (
    <div className="p-5 flex flex-col items-center h-full">
      <Paper
        radius="lg"
        className="max-w-3xl w-full border border-gray-200 overflow-hidden flex-1"
        style={{ position: 'relative' }}
      >
        <LoadingOverlay visible={loading} overlayProps={{ blur: 2 }} />

        {/* 头部 */}
        <div className="px-5 py-4 border-b border-gray-100">
          <Group justify="space-between" align="center">
            <Group gap={8}>
              <IconBell size={18} color="var(--mantine-color-gray-6)" />
              <Text fw={600} size="lg">
                {t('messageCenter.title')}
              </Text>
              {pendingInvitations.length > 0 && (
                <Badge size="sm" color="violet" variant="filled" circle>
                  {pendingInvitations.length}
                </Badge>
              )}
            </Group>
          </Group>
          {companyId && (
            <Group gap={6} mt={8}>
              <IconInfoCircle size={13} color="var(--mantine-color-orange-5)" />
              <Text size="xs" c="orange.5">
                {t('messageCenter.hasOrgTip')}
              </Text>
            </Group>
          )}
        </div>

        {error && (
          <div className="px-4 pt-3">
            <Alert
              color="red"
              icon={<IconX size={14} />}
              onClose={() => setError('')}
              withCloseButton
              radius="md"
              py="xs"
            >
              <Text size="sm">{error}</Text>
            </Alert>
          </div>
        )}

        {/* 列表 */}
        {pendingInvitations.length === 0 && !loading ? (
          <Center className="py-16">
            <Stack align="center" gap="xs">
              <IconInbox size={32} stroke={1.2} color="var(--mantine-color-gray-3)" />
              <Text c="gray.4" size="sm">
                {t('messageCenter.noInvitations')}
              </Text>
            </Stack>
          </Center>
        ) : (
          <div className="p-3 space-y-1.5">
            {pendingInvitations.map((invitation) => (
              <div
                key={invitation.id}
                className="flex items-center gap-3 px-3 py-3 rounded-lg bg-gray-50/60 hover:bg-violet-50/60 hover:translate-x-1 transition-all duration-200 cursor-default"
              >
                {/* 头像 */}
                <div className="relative flex-shrink-0">
                  <Avatar size={34} radius="xl" color="violet" variant="light">
                    <IconBuilding size={15} />
                  </Avatar>
                  {invitation.status === 'pending' && (
                    <div className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-violet-500 rounded-full border-[1.5px] border-white" />
                  )}
                </div>

                {/* 内容 */}
                <div className="flex-1 min-w-0">
                  <Text
                    size="sm"
                    className="leading-snug"
                    c="gray.7"
                    dangerouslySetInnerHTML={{
                      __html: t('messageCenter.inviteDesc', { name: `<strong>${invitation.inviter_name}</strong>` }),
                    }}
                  />
                  <Text size="xs" c="gray.4" mt={2}>
                    {dayjs.utc(invitation.created_at).format('YYYY-MM-DD HH:mm')}
                  </Text>
                </div>

                {/* 操作 */}
                {invitation.status === 'pending' && (
                  <Button
                    color="violet"
                    size="xs"
                    radius="md"
                    variant={companyId ? 'light' : 'filled'}
                    onClick={() => handleAccept(invitation)}
                    loading={processingId === invitation.id}
                    disabled={(processingId !== null && processingId !== invitation.id) || !!companyId}
                    className="flex-shrink-0"
                  >
                    {t('messageCenter.accept')}
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </Paper>
    </div>
  );
};

export default MessageCenter;
